import { useState } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const looksLikeEmail = (value) => EMAIL_RE.test(value);

/**
 * Chip-style multi-email input. Pressing Enter, Tab, comma or semicolon
 * commits the pending input as a chip. Invalid input keeps the typed value
 * visible and surfaces a single-line error so the retailer can fix it.
 *
 * `value` is the controlled array of emails; `onCommit` fires with the new
 * full array whenever a chip is added or removed (one network write per
 * discrete change, never on each keystroke).
 */
// Normalise whatever shape the channel record happens to carry into a string[].
// `config.notificationEmails` has historically been written as an array, a
// comma-separated string and (very early) a single string — coerce all three
// shapes so an older record can't crash this component at render time.
const toEmailArray = (value) => {
	if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
	if (typeof value === 'string')
		return value
			.split(',')
			.map((entry) => entry.trim())
			.filter(Boolean);
	return [];
};

export function MultipleEmailsInput({ value, onCommit, disabled = false, id }) {
	const emails = toEmailArray(value);
	const [draft, setDraft] = useState('');
	const [invalid, setInvalid] = useState(false);

	const commitDraft = () => {
		const trimmed = draft.trim().replace(/[,;]+$/, '');
		if (!trimmed) {
			setDraft('');
			setInvalid(false);
			return;
		}
		if (!looksLikeEmail(trimmed)) {
			setInvalid(true);
			return;
		}
		if (emails.includes(trimmed)) {
			setDraft('');
			setInvalid(false);
			return;
		}
		onCommit([...emails, trimmed]);
		setDraft('');
		setInvalid(false);
	};

	const removeAt = (index) => {
		if (disabled) return;
		const next = emails.filter((_, i) => i !== index);
		onCommit(next);
	};

	const handleKeyDown = (event) => {
		if (['Enter', 'Tab', ',', ';'].includes(event.key)) {
			if (draft.trim()) {
				event.preventDefault();
				commitDraft();
			}
		} else if (event.key === 'Backspace' && !draft && emails.length > 0) {
			removeAt(emails.length - 1);
		} else {
			setInvalid(false);
		}
	};

	return (
		<div className="space-y-1.5">
			<div
				className={cn(
					'flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5',
					'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
					disabled && 'cursor-not-allowed opacity-50'
				)}
			>
				{emails.map((email, index) => (
					<span
						key={`${email}-${index}`}
						className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground"
					>
						{email}
						<button
							type="button"
							onClick={() => removeAt(index)}
							disabled={disabled}
							aria-label={`Remove ${email}`}
							className="rounded-full text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed"
						>
							<X className="h-3 w-3" />
						</button>
					</span>
				))}
				<input
					id={id}
					type="text"
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={commitDraft}
					disabled={disabled}
					placeholder={emails.length === 0 ? 'orders@yourstore.com, manager@yourstore.com' : ''}
					className="flex-1 min-w-[160px] border-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
					data-testid="multiple-emails-input"
				/>
			</div>
			<p className="text-xs text-muted-foreground">Press Enter, Tab or comma to add an address.</p>
			{invalid ? (
				<p className="text-xs font-semibold text-destructive" data-testid="multiple-emails-input-error">
					That doesn't look like a valid email address.
				</p>
			) : null}
		</div>
	);
}
